import type { Locale } from "./locale-config";
import type { MessageKey } from "./messages";

// A small synchronous subset for non-React callers that can run before locale chunks load.
// Regression tests require these values to match the canonical message dictionaries.
const en = {
  "dailyChallenge.answerPrefix": "Answer",
  "dailyChallenge.chainComplete": "Chain complete.",
  "dailyChallenge.correct": "Correct.",
  "dailyChallenge.correctAnswers": "Correct answers",
  "dailyChallenge.higherValueInstruction": "Pick who has the higher value. A wrong answer ends the chain.",
  "dailyChallenge.imposterInstruction": "Select the exact set of correct answers.",
  "dailyChallenge.matchup": "Matchup",
  "dailyChallenge.pick": "Pick",
  "dailyChallenge.round": "Round",
  "dailyChallenge.roundFailed": "Round failed.",
  "dailyChallenge.score": "Score",
  "common.submit": "Submit",
  "dailyChallenge.submitSelection": "Submit Selection",
  "dailyChallenge.typePlayerName": "Type the player name",
  "dailyChallenge.typeYourAnswer": "Type your answer",
  "auth.sessionEnded": "Your session has ended",
  "notifications.challengeReceivedToast": "Challenge from {name}",
  "notifications.challengeAccepted": "Challenge accepted",
  "notifications.challengeDeclined": "Challenge declined",
  "notifications.challengeExpired": "Challenge expired",
  "errors.INSUFFICIENT_TICKETS": "Not enough tickets"
} as const satisfies Partial<Record<MessageKey, string>>;

const es = {
  "dailyChallenge.answerPrefix": "Respuesta",
  "dailyChallenge.chainComplete": "Cadena completada.",
  "dailyChallenge.correct": "Correcto.",
  "dailyChallenge.correctAnswers": "Respuestas correctas",
  "dailyChallenge.higherValueInstruction": "Elige quién tiene mayor valor. Un fallo rompe la cadena.",
  "dailyChallenge.imposterInstruction": "Selecciona el conjunto exacto de respuestas correctas.",
  "dailyChallenge.matchup": "Enfrentamiento",
  "dailyChallenge.pick": "Elegir",
  "dailyChallenge.round": "Ronda",
  "dailyChallenge.roundFailed": "Ronda fallida.",
  "dailyChallenge.score": "Puntuación",
  "common.submit": "Enviar",
  "dailyChallenge.submitSelection": "Enviar selección",
  "dailyChallenge.typePlayerName": "Escribe el nombre del jugador",
  "dailyChallenge.typeYourAnswer": "Escribe tu respuesta",
  "auth.sessionEnded": "Tu sesión ha finalizado",
  "notifications.challengeReceivedToast": "Desafío de {name}",
  "notifications.challengeAccepted": "Desafío aceptado",
  "notifications.challengeDeclined": "Desafío rechazado",
  "notifications.challengeExpired": "Desafío caducado",
  "errors.INSUFFICIENT_TICKETS": "No tienes suficientes tickets"
} satisfies Record<keyof typeof en, string>;

const ka = {
  "dailyChallenge.answerPrefix": "პასუხი",
  "dailyChallenge.chainComplete": "ჯაჭვი დასრულებულია.",
  "dailyChallenge.correct": "სწორია.",
  "dailyChallenge.correctAnswers": "სწორი პასუხები",
  "dailyChallenge.higherValueInstruction": "აირჩიე, ვის აქვს უფრო მაღალი მაჩვენებელი. არასწორი პასუხი წყვეტს ჯაჭვს.",
  "dailyChallenge.imposterInstruction": "აირჩიე სწორი პასუხების ზუსტი ნაკრები.",
  "dailyChallenge.matchup": "დაპირისპირება",
  "dailyChallenge.pick": "აირჩიე",
  "dailyChallenge.round": "რაუნდი",
  "dailyChallenge.roundFailed": "რაუნდი ჩაიშალა.",
  "dailyChallenge.score": "ქულა",
  "common.submit": "გაგზავნა",
  "dailyChallenge.submitSelection": "არჩევანის დადასტურება",
  "dailyChallenge.typePlayerName": "ჩაწერე მოთამაშის სახელი",
  "dailyChallenge.typeYourAnswer": "ჩაწერე შენი პასუხი",
  "auth.sessionEnded": "თქვენი სესია დასრულდა",
  "notifications.challengeReceivedToast": "გამოწვევა {name}-სგან",
  "notifications.challengeAccepted": "გამოწვევა მიღებულია",
  "notifications.challengeDeclined": "გამოწვევა უარყოფილია",
  "notifications.challengeExpired": "გამოწვევის ვადა ამოიწურა",
  "errors.INSUFFICIENT_TICKETS": "არ გაქვთ საკმარისი ბილეთები"
} satisfies Record<keyof typeof en, string>;

const tr = {
  "dailyChallenge.answerPrefix": "Cevap",
  "dailyChallenge.chainComplete": "Zincir tamamlandı.",
  "dailyChallenge.correct": "Doğru.",
  "dailyChallenge.correctAnswers": "Doğru cevaplar",
  "dailyChallenge.higherValueInstruction": "Daha yüksek değere sahip olanı seç. Yanlış cevap zinciri bitirir.",
  "dailyChallenge.imposterInstruction": "Doğru cevapların tam setini seç.",
  "dailyChallenge.matchup": "Eşleşme",
  "dailyChallenge.pick": "Seç",
  "dailyChallenge.round": "Tur",
  "dailyChallenge.roundFailed": "Tur başarısız oldu.",
  "dailyChallenge.score": "Skor",
  "common.submit": "Gönder",
  "dailyChallenge.submitSelection": "Seçimi Gönder",
  "dailyChallenge.typePlayerName": "Oyuncu adını yaz",
  "dailyChallenge.typeYourAnswer": "Cevabını yaz",
  "auth.sessionEnded": "Oturumun sona erdi",
  "notifications.challengeReceivedToast": "{name} tarafından meydan okuma",
  "notifications.challengeAccepted": "Meydan okuma kabul edildi",
  "notifications.challengeDeclined": "Meydan okuma reddedildi",
  "notifications.challengeExpired": "Meydan okuma süresi doldu",
  "errors.INSUFFICIENT_TICKETS": "Yeterli bilet yok"
} satisfies Record<keyof typeof en, string>;

const ru = {
  "dailyChallenge.answerPrefix": "Ответ",
  "dailyChallenge.chainComplete": "Цепочка завершена.",
  "dailyChallenge.correct": "Верно.",
  "dailyChallenge.correctAnswers": "Верные ответы",
  "dailyChallenge.higherValueInstruction": "Выбери, у кого показатель выше. Ошибка прерывает цепочку.",
  "dailyChallenge.imposterInstruction": "Выбери точный набор верных ответов.",
  "dailyChallenge.matchup": "Противостояние",
  "dailyChallenge.pick": "Выбор",
  "dailyChallenge.round": "Раунд",
  "dailyChallenge.roundFailed": "Раунд провален.",
  "dailyChallenge.score": "Счет",
  "common.submit": "Отправить",
  "dailyChallenge.submitSelection": "Подтвердить выбор",
  "dailyChallenge.typePlayerName": "Введи имя игрока",
  "dailyChallenge.typeYourAnswer": "Введи свой ответ",
  "auth.sessionEnded": "Твоя сессия завершена",
  "notifications.challengeReceivedToast": "Вызов от {name}",
  "notifications.challengeAccepted": "Вызов принят",
  "notifications.challengeDeclined": "Вызов отклонен",
  "notifications.challengeExpired": "Срок вызова истек",
  "errors.INSUFFICIENT_TICKETS": "Недостаточно билетов"
} satisfies Record<keyof typeof en, string>;

const pt = {
  "dailyChallenge.answerPrefix": "Resposta",
  "dailyChallenge.chainComplete": "Sequência concluída.",
  "dailyChallenge.correct": "Correto.",
  "dailyChallenge.correctAnswers": "Respostas corretas",
  "dailyChallenge.higherValueInstruction": "Escolha quem tem o valor mais alto. Uma resposta errada encerra a sequência.",
  "dailyChallenge.imposterInstruction": "Selecione o conjunto exato de respostas corretas.",
  "dailyChallenge.matchup": "Confronto",
  "dailyChallenge.pick": "Escolha",
  "dailyChallenge.round": "Rodada",
  "dailyChallenge.roundFailed": "Rodada perdida.",
  "dailyChallenge.score": "Pontuação",
  "common.submit": "Enviar",
  "dailyChallenge.submitSelection": "Enviar seleção",
  "dailyChallenge.typePlayerName": "Digite o nome do jogador",
  "dailyChallenge.typeYourAnswer": "Digite sua resposta",
  "auth.sessionEnded": "Sua sessão terminou",
  "notifications.challengeReceivedToast": "Desafio de {name}",
  "notifications.challengeAccepted": "Desafio aceito",
  "notifications.challengeDeclined": "Desafio recusado",
  "notifications.challengeExpired": "Desafio expirado",
  "errors.INSUFFICIENT_TICKETS": "Ingressos insuficientes"
} satisfies Record<keyof typeof en, string>;

const de = {
  "dailyChallenge.answerPrefix": "Antwort",
  "dailyChallenge.chainComplete": "Serie abgeschlossen.",
  "dailyChallenge.correct": "Richtig.",
  "dailyChallenge.correctAnswers": "Richtige Antworten",
  "dailyChallenge.higherValueInstruction": "Wähle, wer den höheren Wert hat. Eine falsche Antwort beendet die Serie.",
  "dailyChallenge.imposterInstruction": "Wähle genau die richtigen Antworten aus.",
  "dailyChallenge.matchup": "Match",
  "dailyChallenge.pick": "Wähle",
  "dailyChallenge.round": "Runde",
  "dailyChallenge.roundFailed": "Runde fehlgeschlagen.",
  "dailyChallenge.score": "Punktzahl",
  "common.submit": "Absenden",
  "dailyChallenge.submitSelection": "Auswahl absenden",
  "dailyChallenge.typePlayerName": "Gib den Spielernamen ein",
  "dailyChallenge.typeYourAnswer": "Gib deine Antwort ein",
  "auth.sessionEnded": "Deine Sitzung ist beendet",
  "notifications.challengeReceivedToast": "Challenge von {name}",
  "notifications.challengeAccepted": "Challenge angenommen",
  "notifications.challengeDeclined": "Challenge abgelehnt",
  "notifications.challengeExpired": "Challenge abgelaufen",
  "errors.INSUFFICIENT_TICKETS": "Nicht genug Tickets"
} satisfies Record<keyof typeof en, string>;

export type RuntimeMessageKey = keyof typeof en;
export const RUNTIME_MESSAGE_KEYS = Object.keys(en) as RuntimeMessageKey[];
const dictionaries: Record<Locale, Readonly<Record<RuntimeMessageKey, string>>> = { en, es, ka, tr, ru, pt, de };

export function translateRuntimeCopy(locale: Locale, key: RuntimeMessageKey, params?: Record<string, string | number>): string {
  const template = dictionaries[locale][key];
  return params ? template.replace(/\{(\w+)\}/g, (match, name: string) => params[name] === undefined ? match : String(params[name])) : template;
}
